import { ToolbarDefault, convertHtmlStringValueToPlainText, nodeListToArray } from '@AppModels/UiUtils';
import { DefaultRawRichTextContentBlockStyleAttribute, IDictionary, UserSessionContext, VmUtils } from '@ViewModels';
import { Editor } from '@tinymce/tinymce-react';
import { inject } from 'mobx-react';
import outy, { IOutyHandle } from 'outy';
import * as React from 'react';
import * as tinymce from 'tinymce';
import {
	IImpersonationContextComponentProps,
	IRichContentDocumentEditorImageOptions,
	IRichContentDocumentEditorPlaceholder,
	ImpersonationContextKey,
} from '../../../../models';
import { AppState, ErrorMessagesViewModelKey } from '../../../../models/AppState';
import { IEmailComposerContextChildProps, withEmailComposerContext } from '../../../../models/Email';
import { Token, TokenAttributes } from '../../../../models/Token';
import { navigation } from '../../../styles/colors';
import ElementDtd from './elements.dtd.json';
import {
	AlignmentMenuToolbarPlugin,
	FileAttachmentToolbarPlugin,
	GiphyToolbarPlugin,
	InsertImageToolbarPlugin,
	InsertVideoToolbarPlugin,
	InsertMenuSignatureToolbarPlugin,
	InsertMenuToolbarPlugin,
	ListMenuToolbarPlugin,
	MeetingMergeFieldMenuToolbarPlugin,
	MergeFieldMenuToolbarPlugin,
	PlaceholdersPlugin,
	SurveyLinksPlugin,
	parsePastedContent,
} from './plugins';

interface IHtmlEditorDtd {
	attrMap: IDictionary<string[]>;
	commonAttrs: string[];
	customElements: string[];
	elements: string[];
}

const PlaceholderDataId = 'htmlEditorPlaceholderText';
const Dtd: IHtmlEditorDtd = ElementDtd as any;
const ImgTagFilterRegExp = /<img[^>]*src="(?!(https|data):\/\/)[^"]*"[^>]*\/?>/gim;

interface IHtmlEditorInit extends tinymce.RawEditorSettings {
	skipDefaultEmailStyleFont?: boolean;
}

interface IProps extends IImpersonationContextComponentProps, IEmailComposerContextChildProps {
	autoFocus?: boolean;
	contentPlaceholderText?: string;
	disablePasteRichText?: boolean;
	imageOptions?: IRichContentDocumentEditorImageOptions;
	init?: IHtmlEditorInit;
	onBlur?(
		e: tinymce.EditorEvent<{
			focusedEditor: tinymce.Editor;
		}>
	): void;
	onEditorChanged?(contentHtmlStringValue: string, editorRef: tinymce.Editor): void;
	onExecuteCommand?(e: tinymce.Events.ExecCommandEvent): void;
	onFocus?(
		e: tinymce.EditorEvent<{
			blurredEditor: tinymce.Editor;
		}>
	): void;
	onLoad?(editorRef: tinymce.Editor): void;
	readOnly?: boolean;
	userSession: UserSessionContext;
	value?: string;
}

interface IState {
	showingPlaceholderText?: boolean;
	value?: string;
}

const DefaultTinyMceInit: tinymce.RawEditorSettings = {
	branding: false,
	browser_spellcheck: true,
	content_style: `
		body {
			background: transparent;
			margin: 0;
			padding: 10px;
		}
		h1, h2, h3, h4, h5, h6, p {
			margin: 0px;
			padding: 0px;
		}
	`,
	convert_urls: false,
	fontsize_formats: '10px 11px 12px 14px 16px 18px 24px 36px 48px',
	forced_root_block_attrs: {
		style: DefaultRawRichTextContentBlockStyleAttribute,
	},
	height: '100%',
	link_assume_external_targets: 'https',
	link_context_toolbar: true,
	menubar: false,
	paste_data_images: false,
	paste_retain_style_properties:
		'color font-size font-weight font-style font-family border padding margin width height text-align',
	plugins: 'lists paste link placeholders noneditable table',
	quickbars_insert_toolbar: false,
	quickbars_selection_toolbar: false,
	resize: false,
	statusbar: false,
	toolbar: ToolbarDefault,
};

export const ValidElementsStringValue = Dtd.elements.reduce((result, element) => {
	const attrsStringValue = [...Dtd.commonAttrs, ...(Dtd.attrMap[element] || [])].filter(x => !!x).join('|');
	const nextResult = `${result}${result ? ',' : ''}${element}${attrsStringValue ? `[${attrsStringValue}]` : ''}`;
	return nextResult;
}, '');

// Helper function to generate account-specific HTML comment marker
const generateAccountSpecificMarker = (accountId: string | undefined): string => {
	// Use first 4 characters of account ID, or 'NONE' if no account ID
	const prefix = accountId ? accountId.substring(0, 4) : 'NONE';
	return `<!-- Levitate Font Preferences Applied [${prefix}] -->`;
};

// Helper function to check if content has account-specific marker
const hasAccountSpecificMarker = (content: string, accountId: string | undefined): boolean => {
	const prefix = accountId ? accountId.substring(0, 4) : 'NONE';
	// Escape special regex characters in the prefix
	const escapedPrefix = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	const markerPattern = new RegExp(`<!-- Levitate Font Preferences Applied \\[${escapedPrefix}\\] -->`);
	return markerPattern.test(content);
};

class HtmlEditorBase extends React.Component<IProps, IState> {
	public static defaultProps: Partial<IProps> = {
		disablePasteRichText: false,
	};

	// Track if defaults have been applied to empty editor to prevent reapplication
	private hasAppliedDefaultsToEmpty = false;

	private outsideClickHandle: IOutyHandle;

	private mEditorRef: tinymce.Editor;

	private mMounted: boolean;
	private mSettings: tinymce.RawEditorSettings;
	public readonly state: IState = {};
	private mPluginDisposers: (() => void)[];
	private lastProcessingTime: number;

	public static getDerivedStateFromProps(props: IProps, state: IState) {
		const nextState: IState = {};

		if (state.value !== props.value) {
			nextState.value = props.value || '';
		}

		return Object.keys(nextState).length > 0 ? nextState : null;
	}

	constructor(props: IProps) {
		super(props);

		this.mPluginDisposers = [];
		const { init, value, imageOptions } = this.props;

		const initSettings = { ...init, ...{ paste_data_images: imageOptions?.allowPasteImages } };

		const settings: tinymce.RawEditorSettings = {
			...DefaultTinyMceInit,
			...initSettings,
			// set this to the output path for the tinymce themes, plugins, etc.
			// need to cast to override readonly
			base_url: `/static/tinymce`,
			contextmenu: false,
			default_link_target: '_blank',
			setup: this.onSetup,
			valid_elements: ValidElementsStringValue,
		};
		this.mSettings = settings;
		this.state = {
			value,
		};
	}

	public componentWillUnmount() {
		this.mMounted = false;
		if (this.mEditorRef) {
			this.mEditorRef.off('blur', this.onBlur);
			this.mEditorRef.off('ExecCommand', this.onExecuteCommand);
			this.mEditorRef.off('focus', this.onFocus);
			this.mEditorRef.off('keyup', this.onKeyUp);
			this.mEditorRef.off('init', this.onEditorInit);
			this.mEditorRef.off('PastePreProcess', this.onContentPastePreProcess);
			this.mEditorRef.off('NodeChange', this.onNodeChange);
			this.mEditorRef.off('SetContent', this.onSetContent);
			this.unRegisterClickOutside();
		}
		(this.mPluginDisposers || []).forEach(x => x());

		this.mPluginDisposers = null;
	}

	public render() {
		const { value } = this.state;
		return <Editor init={this.mSettings} onEditorChange={this.onEditorChanged} value={value || undefined} />;
	}

	private onEditorChanged = (contentHtmlStringValue: string) => {
		const { onEditorChanged } = this.props;
		const { showingPlaceholderText } = this.state;
		if (contentHtmlStringValue !== this.state.value && !showingPlaceholderText) {
			if (onEditorChanged) {
				onEditorChanged(contentHtmlStringValue, this.mEditorRef);
			} else {
				this.setState({
					value: contentHtmlStringValue,
				});
			}
		}
	};

	private onSetContent = () => {
		// When content is set (like when templates are loaded), reapply font defaults
		// Use a longer delay to ensure TinyMCE is fully initialized, especially for color commands
		setTimeout(() => {
			this.applyDefaultFontsToAllContent();
		}, 250);
	};

	private onSetup = (editor: tinymce.Editor) => {
		const { init, userSession, impersonationContext, imageOptions, emailComposerContext } = this.props;
		this.mEditorRef = editor;

		// add on event handlers
		this.mEditorRef.on('blur', this.onBlur);
		this.mEditorRef.on('ExecCommand', this.onExecuteCommand);
		this.mEditorRef.on('focus', this.onFocus);
		this.mEditorRef.on('init', this.onEditorInit);
		this.mEditorRef.on('keyup', this.onKeyUp);
		this.mEditorRef.on('PastePreProcess', this.onContentPastePreProcess);
		this.mEditorRef.on('NodeChange', this.onNodeChange);
		this.mEditorRef.on('SetContent', this.onSetContent);
		this.mEditorRef.addCommand('levError', VmUtils.Noop);

		// Check if meeting merge fields toolbar is enabled
		const isMeetingMergeFieldsEnabled =
			init?.toolbar &&
			(typeof init.toolbar === 'string'
				? init.toolbar.includes('meetingmergefieldmenu')
				: Array.isArray(init.toolbar)
					? init.toolbar.includes('meetingmergefieldmenu')
					: false);

		[
			FileAttachmentToolbarPlugin.onInit(userSession, this.mEditorRef, impersonationContext) || null,
			InsertImageToolbarPlugin.onInit(userSession, this.mEditorRef, impersonationContext, imageOptions) || null,
			InsertVideoToolbarPlugin.onInit(userSession, this.mEditorRef, impersonationContext) || null,

			PlaceholdersPlugin.onInit(userSession, this.mEditorRef, impersonationContext) || null,

			SurveyLinksPlugin.onInit(userSession, this.mEditorRef, impersonationContext) || null,

			GiphyToolbarPlugin.onInit(userSession, this.mEditorRef, impersonationContext) || null,
			InsertMenuToolbarPlugin.onInit(userSession, this.mEditorRef, impersonationContext, imageOptions) || null,
			InsertMenuSignatureToolbarPlugin.onInit(userSession, this.mEditorRef, impersonationContext, imageOptions) || null,

			ListMenuToolbarPlugin.onInit(userSession, this.mEditorRef, impersonationContext) || null,

			AlignmentMenuToolbarPlugin.onInit(userSession, this.mEditorRef, impersonationContext) || null,

			MergeFieldMenuToolbarPlugin.onInitWithRenewalFlowContext(
				userSession,
				this.mEditorRef,
				emailComposerContext,
				impersonationContext
			) || null,

			// Initialize Meeting Merge Fields plugin if enabled
			isMeetingMergeFieldsEnabled
				? MeetingMergeFieldMenuToolbarPlugin.onInit(userSession, this.mEditorRef, impersonationContext) || null
				: null,
		].forEach(x => {
			if (x) {
				this.mPluginDisposers.push(x);
			}
		});

		if (!!init && init.setup) {
			init.setup(editor);
		}
	};

	private onExecuteCommand = (e: tinymce.Events.ExecCommandEvent) => {
		const { onExecuteCommand } = this.props;
		if (onExecuteCommand) {
			onExecuteCommand(e);
		}
	};

	private onEditorInit = () => {
		const { autoFocus, onLoad } = this.props;
		this.mMounted = true;
		this.registerClickOutside();

		// Apply default fonts after a short delay to ensure content is loaded
		setTimeout(() => {
			if (!!this.mMounted && !!this.mEditorRef) {
				this.applyDefaultFontsToAllContent();
			}
		}, 100);

		if (autoFocus) {
			setTimeout(() => {
				if (!!this.mMounted && !!this.mEditorRef) {
					this.mEditorRef.focus();
					this.togglePlaceholderText(false);
				}
			});
		} else {
			this.togglePlaceholderText(true);
		}

		if (onLoad) {
			onLoad(this.mEditorRef);
		}
	};

	private onFocus = (
		e: tinymce.EditorEvent<{
			blurredEditor: tinymce.Editor;
		}>
	) => {
		const { onFocus } = this.props;
		this.togglePlaceholderText(false);
		if (onFocus) {
			onFocus(e);
		}
	};

	private onKeyUp = (e: tinymce.EditorEvent<KeyboardEvent>) => {
		// Reset the empty editor flag when user types (content is no longer empty)
		if (this.hasAppliedDefaultsToEmpty) {
			this.hasAppliedDefaultsToEmpty = false;
		}

		const { placeholders } = this.props.init;
		if (placeholders?.length > 0) {
			(placeholders as IRichContentDocumentEditorPlaceholder[]).forEach(placeholder => {
				const symbols = [placeholder.symbol, ...(placeholder.additionalSymbols || [])].filter(x => x.endsWith(e.key));
				// Also fire if it is ] since tinymce has some race condition of releasing the shift key
				if (!!this.mEditorRef && (symbols.length || ']' === e.key)) {
					const content = this.mEditorRef.getContent().toString();
					if (!symbols.some(x => !!content.match(new RegExp(x, 'igm')))) {
						return;
					}
					try {
						const [newContent, addedTokens] = Token.replaceContent(content, placeholder);
						if (newContent) {
							this.mEditorRef.setContent(newContent);
							// set the cursor after the token
							const elements = nodeListToArray(this.mEditorRef.dom.doc.querySelectorAll(`span[data-placeholder=true]`));
							const element =
								(addedTokens?.length === 1
									? elements?.find(x => {
											const id = x.getAttribute(TokenAttributes.DataId);
											return !!id && addedTokens[0].getAttribute(TokenAttributes.DataId) === id;
										})
									: null) || (elements ? elements[elements.length - 1] : null);
							if (element) {
								this.mEditorRef.selection.select(element);

								this.mEditorRef.selection.collapse(false);
							}
						}
					} catch (err) {
						AppState[ErrorMessagesViewModelKey].push({
							// @ts-ignore
							messages: [err.message],
						});
					}
				}
			});
		}
	};

	private onBlur = (
		e: tinymce.EditorEvent<{
			focusedEditor: tinymce.Editor;
		}>
	) => {
		const { onBlur } = this.props;
		this.togglePlaceholderText(true);
		this.dismissToolbarOverflowMenu();
		if (onBlur) {
			onBlur(e);
		}
	};

	private registerClickOutside = () => {
		this.unRegisterClickOutside();
		if (!!this.mEditorRef && !!this.mEditorRef.editorContainer) {
			this.outsideClickHandle = outy(
				[this.mEditorRef.editorContainer],
				['click', 'touchstart'],
				this.dismissToolbarOverflowMenu
			);
		}
	};

	private unRegisterClickOutside = () => {
		if (this.outsideClickHandle) {
			this.outsideClickHandle.remove();

			this.outsideClickHandle = null;
		}
	};

	private dismissToolbarOverflowMenu = () => {
		if (!!this.mEditorRef && !!this.mEditorRef.editorContainer) {
			// find the more button
			const el = this.mEditorRef.editorContainer.querySelector(
				'.tox-toolbar__group button[aria-label="More..."].tox-tbtn'
			) as HTMLElement;
			if (el) {
				const pressed =
					!!el.hasAttribute('aria-expanded') && el.getAttribute('aria-expanded') === 'true' ? true : false;
				if (pressed) {
					// close the menu by clicking
					el.click();
				}
			}
		}
	};

	private onContentPastePreProcess = (
		e: React.ChangeEvent<HTMLElement> & {
			content: string;
			internal: boolean;
			wordContent: boolean;
		}
	) => {
		const { imageOptions } = this.props;

		// If an image tag is the only thing pasted in (in position 0), do not pre-process it
		if (imageOptions?.allowPasteImages && e.content.indexOf('<img') === 0) {
			// If the image is a data:image, it is probably a preview the OS tried to generate, so clear the content
			if (e.content.indexOf('src="data:image') > -1) {
				e.content = '';
			}
			return;
		}
		const { readOnly, disablePasteRichText } = this.props;
		if (!readOnly && !!this.mEditorRef && !!e.content) {
			e.preventDefault();
			if (disablePasteRichText) {
				this.mEditorRef.insertContent(convertHtmlStringValueToPlainText(e.content));
				return;
			}

			let transformed = e.content;

			if (this.mSettings.plugins?.indexOf('surveylinks') >= 0) {
				transformed = SurveyLinksPlugin.onContentPastePreProcess(transformed);
			}

			const { placeholders } = this.props.init;
			if (placeholders && placeholders.length > 0) {
				transformed = Token.replaceImpostorPlaceholders(transformed, placeholders);
			}
			this.mEditorRef.insertContent(parsePastedContent(transformed.replace(ImgTagFilterRegExp, '')));
		}
	};

	private togglePlaceholderText = (show = true) => {
		if (this.mMounted) {
			this.setState(
				{
					showingPlaceholderText: show,
				},
				() => {
					if (this.mMounted) {
						const { contentPlaceholderText } = this.props;
						if (!!contentPlaceholderText && !!this.mEditorRef) {
							if (show) {
								const content = this.mEditorRef.getContent();
								if (!content) {
									this.mEditorRef.setContent(
										`<p data-id="${PlaceholderDataId}" style="color:${navigation};${DefaultTinyMceInit.forced_root_block_attrs.style}">${contentPlaceholderText}</p>`
									);
								}
							} else {
								const placeholder = this.mEditorRef.getDoc().querySelector(`p[data-id="${PlaceholderDataId}"]`);
								if (placeholder) {
									placeholder.parentNode.removeChild(placeholder);
									// add an empty <p>

									this.mEditorRef.setContent(`<p style="${DefaultTinyMceInit.forced_root_block_attrs.style}"></p>`);
								}
							}
						}
					}
				}
			);
		}
	};

	private hasToolbarFontControls = (): boolean => {
		const toolbar = this.mSettings.toolbar;
		if (!toolbar) return false;

		const toolbarString = Array.isArray(toolbar) ? toolbar.join(' ') : toolbar;

		// Check if toolbar includes font selection controls (specifically fontselect)
		return typeof toolbarString === 'string' && toolbarString.includes('fontselect');
	};

	private setDefaultFormattingStyles = (defaults: { fontFamily: string; fontSize: string }) => {
		if (!this.mEditorRef) return;

		try {
			// Just set the formatter state for immediate typing - no CSS for email compatibility
			this.mEditorRef.formatter.apply('fontname', { value: defaults.fontFamily });
			this.mEditorRef.formatter.apply('fontsize', { value: defaults.fontSize });
			// Note: We no longer apply font color
		} catch {
			// Silently handle errors during font formatting
		}
	};

	private updateToolbarDropdowns = () => {
		if (!this.mEditorRef) return;

		try {
			// Use a longer delay to ensure all formatting has been applied
			setTimeout(() => {
				const body = this.mEditorRef.getBody();

				// Find the last text node or element with actual content
				const lastContentNode = this.findLastContentNode(body);

				if (lastContentNode) {
					// Position cursor at the very end of the last content node
					const range = this.mEditorRef.getDoc().createRange();

					if (lastContentNode.nodeType === Node.TEXT_NODE) {
						// For text nodes, position at the end of the text
						range.setStart(lastContentNode, lastContentNode.textContent.length);
						range.setEnd(lastContentNode, lastContentNode.textContent.length);
					} else {
						// For element nodes, position at the end of the element
						range.setStart(lastContentNode, lastContentNode.childNodes.length);
						range.setEnd(lastContentNode, lastContentNode.childNodes.length);
					}

					this.mEditorRef.selection.setRng(range);
				} else {
					// If no content node found, try to clean up empty paragraphs and position cursor properly
					const paragraphs = Array.from(body.querySelectorAll('p')).reverse();

					// Find the last paragraph with actual content
					for (const p of paragraphs) {
						const hasContent = p.textContent?.trim() && p.textContent.trim() !== '﻿';
						if (hasContent) {
							const range = this.mEditorRef.getDoc().createRange();
							range.selectNodeContents(p);
							range.collapse(false); // Position at end
							this.mEditorRef.selection.setRng(range);

							break;
						}
					}
				}

				// Force TinyMCE to update its toolbar state
				this.mEditorRef.nodeChanged();
			}, 100);
		} catch {
			// Silently handle errors during font formatting
		}
	};

	private findLastContentNode = (element: Node): Node | null => {
		// Recursively find the last node that contains actual text content
		// Skip TinyMCE format caret elements and empty paragraphs
		let lastNode = null;

		for (let i = element.childNodes.length - 1; i >= 0; i--) {
			const child = element.childNodes[i];

			if (child.nodeType === Node.TEXT_NODE) {
				const textContent = child.textContent?.trim();
				// Skip zero-width space characters used by TinyMCE
				if (textContent && textContent !== '﻿') {
					return child;
				}
			} else if (child.nodeType === Node.ELEMENT_NODE) {
				const elem = child as Element;

				// Skip TinyMCE format caret elements and bogus elements
				if (
					elem.getAttribute('data-mce-type') === 'format-caret' ||
					elem.getAttribute('data-mce-bogus') === '1' ||
					elem.id === '_mce_caret'
				) {
					continue;
				}

				// Skip empty paragraphs with just <br> tags
				if (elem.tagName === 'P' && elem.innerHTML === '<br>') {
					continue;
				}

				// Recursively search in child elements
				const childResult = this.findLastContentNode(child);
				if (childResult) {
					return childResult;
				}

				// If no text content in children but element has meaningful content, use it
				const elementText = elem.textContent?.trim();
				if (elementText && elementText !== '﻿' && !lastNode) {
					lastNode = child;
				}
			}
		}

		return lastNode;
	};

	private applyDefaultFontsToAllContent = () => {
		if (!this.mEditorRef) {
			return;
		}

		// Get defaults from account's defaultEmailStyle feature (respecting impersonation)
		const { userSession, impersonationContext, init } = this.props;
		const account = impersonationContext?.account || userSession?.account;
		const defaultEmailStyle = account?.features?.defaultEmailStyle;

		// Skip if the skipDefaultEmailStyleFont flag is set (e.g., in signature editor to prevent UI issues)
		if (init?.skipDefaultEmailStyleFont) {
			return;
		}

		// Only apply if feature is enabled AND editor has font controls
		if (!defaultEmailStyle?.enabled || !this.hasToolbarFontControls()) {
			return;
		}

		// Check if editor is completely empty
		const content = this.mEditorRef.getContent();
		const isEmpty = !content || content.trim() === '' || content === '<p></p>' || content === '<p><br></p>';

		// Check if content already has the account-specific marker indicating user has overridden preferences
		const accountId = account?.id;
		if (content && hasAccountSpecificMarker(content, accountId)) {
			// User has manually changed fonts for this account - don't reapply defaults
			return;
		}

		// For empty editors, check if we've already applied defaults to prevent loops
		if (isEmpty && this.hasAppliedDefaultsToEmpty) {
			return;
		}

		// Use a simple processing flag that resets with new content but persists during multiple SetContent events
		const currentTime = Date.now();
		if (!this.lastProcessingTime) {
			this.lastProcessingTime = 0;
		}

		// If we processed content very recently (within 500ms), skip to prevent duplicate processing
		const timeSinceLastProcessing = currentTime - this.lastProcessingTime;
		if (timeSinceLastProcessing < 500) {
			// Still update toolbar in case it's not reflecting current state
			setTimeout(() => {
				this.updateToolbarDropdowns();
			}, 50);
			return;
		}

		const defaults = {
			fontFamily: defaultEmailStyle.fontFace || 'Arial, sans-serif',
			fontSize: defaultEmailStyle.fontSize || '14px',
		};

		try {
			if (isEmpty) {
				// For empty editors, just set the formatting context without creating visible content
				// This will apply to whatever content the user types next
				this.mEditorRef.formatter.apply('fontname', { value: defaults.fontFamily });
				this.mEditorRef.formatter.apply('fontsize', { value: defaults.fontSize });
				// Note: We no longer apply font color

				// Mark that we've applied defaults to empty editor
				this.hasAppliedDefaultsToEmpty = true;
			} else {
				// For editors with content, select all and apply fonts
				this.mEditorRef.execCommand('SelectAll');

				// Apply font family
				this.mEditorRef.execCommand('FontName', false, defaults.fontFamily);

				// Apply font size
				this.mEditorRef.execCommand('FontSize', false, defaults.fontSize);

				// Note: We no longer apply font color

				// Clear selection and position cursor at the end of content
				this.mEditorRef.selection.collapse(false);
			}

			// Set default formatting for new content (paragraphs, etc.)
			// Only do this for non-empty editors to avoid extra formatting calls
			if (!isEmpty) {
				this.setDefaultFormattingStyles(defaults);
			}

			// Update toolbar dropdowns to reflect the applied settings
			// Use a small delay to ensure toolbar is fully rendered
			setTimeout(() => {
				this.updateToolbarDropdowns();
			}, 50);

			// Handle merge field placeholders separately since they might have specific styling
			this.applyDefaultsToMergeFields(defaults);

			// Add account-specific HTML comment marker to indicate preferences have been applied
			// This prevents reapplication if user manually changes fonts for this account
			// Only add marker for non-empty content (empty editors use the flag instead)
			if (!isEmpty) {
				const currentContent = this.mEditorRef.getContent();
				const accountMarker = generateAccountSpecificMarker(accountId);

				if (!hasAccountSpecificMarker(currentContent, accountId)) {
					const markedContent = currentContent + accountMarker;
					this.mEditorRef.setContent(markedContent);
				}
			}

			// Mark the processing timestamp to prevent duplicate processing
			this.lastProcessingTime = Date.now();

			// Manually trigger the editor changed callback to update content references
			// This ensures the system recognizes the styled content as "user-modified"
			if (this.props.onEditorChanged) {
				const finalContent = this.mEditorRef.getContent();
				this.props.onEditorChanged(finalContent, this.mEditorRef);
			}
		} catch {
			// Silently handle errors during font formatting
		}
	};

	private onNodeChange = () => {
		// Check for newly added merge fields and apply defaults
		if (this.mEditorRef) {
			// Get defaults from account's defaultEmailStyle feature (respecting impersonation)
			const { userSession, impersonationContext } = this.props;
			const account = impersonationContext?.account || userSession?.account;
			const defaultEmailStyle = account?.features?.defaultEmailStyle;

			// Only apply if feature is enabled AND editor has font controls
			if (defaultEmailStyle?.enabled && this.hasToolbarFontControls()) {
				setTimeout(() => {
					this.applyDefaultsToMergeFields({
						fontFamily: defaultEmailStyle.fontFace || 'Arial, sans-serif',
						fontSize: defaultEmailStyle.fontSize || '14px',
					});
				}, 10); // Small delay to ensure the merge field is fully inserted
			}
		}
	};

	private applyDefaultsToMergeFields = (defaults: { fontFamily: string; fontSize: string }) => {
		if (!this.mEditorRef) {
			return;
		}

		try {
			const body = this.mEditorRef.getBody();

			// Look for merge field placeholders - they typically have data-placeholder attribute or specific classes
			const mergeFields = body.querySelectorAll(
				'span[data-placeholder="true"], [data-placeholder], .placeholder, span[contenteditable="false"]'
			) as NodeListOf<HTMLElement>;

			mergeFields.forEach(field => {
				// Always apply styles to merge fields since they need to be in the final HTML for email rendering
				// This ensures when the server replaces {{FirstName}} with "John", "John" will have these styles
				field.style.fontFamily = defaults.fontFamily;
				field.style.fontSize = defaults.fontSize;
				// Note: We no longer apply font color

				// Also apply to any child elements within the merge field
				const childElements = field.querySelectorAll('*') as NodeListOf<HTMLElement>;
				childElements.forEach(child => {
					child.style.fontFamily = defaults.fontFamily;
					child.style.fontSize = defaults.fontSize;
					// Note: We no longer apply font color
				});
			});
		} catch {
			// Silently handle any errors during merge field styling
		}
	};
}

const HtmlEditorWithEmailComposerContext = withEmailComposerContext(HtmlEditorBase);
const HtmlEditor = inject(ImpersonationContextKey)(HtmlEditorWithEmailComposerContext);
// eslint-disable-next-line import/no-default-export
export default HtmlEditor;
